import React from "react";
import type { DriverAnalyticsSummary } from "../../types/driverDeliveries";

type DriverDeliveriesAnalyticsProps = {
  analytics: DriverAnalyticsSummary;
};

type Segment = {
  label: string;
  value: number;
  color: string;
};

const cardStyle: React.CSSProperties = {
  border: "1px solid var(--border)",
  borderRadius: "24px",
  background: "var(--surface)",
  boxShadow: "var(--shadow)",
  padding: "18px",
  minHeight: "100%",
};

const titleStyle: React.CSSProperties = {
  fontSize: "18px",
  fontWeight: 800,
  color: "var(--text-primary)",
};

const subtitleStyle: React.CSSProperties = {
  fontSize: "13px",
  color: "var(--text-secondary)",
  marginTop: "4px",
};

const chartWrapStyle: React.CSSProperties = {
  marginTop: "18px",
  display: "flex",
  justifyContent: "center",
};

const centerTextWrapStyle: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  pointerEvents: "none",
};

const centerLabelStyle: React.CSSProperties = {
  fontSize: "13px",
  color: "var(--text-secondary)",
  marginBottom: "6px",
};

const centerValueStyle: React.CSSProperties = {
  fontSize: "28px",
  fontWeight: 900,
  color: "var(--text-primary)",
};

const legendStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: "12px",
  marginTop: "18px",
};

const legendCardStyle: React.CSSProperties = {
  border: "1px solid var(--border)",
  borderRadius: "18px",
  background: "var(--surface-alt)",
  padding: "12px 14px",
};

const legendLabelRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  marginBottom: "6px",
};

const legendDotStyle = (color: string): React.CSSProperties => ({
  width: "12px",
  height: "12px",
  borderRadius: "999px",
  background: color,
  flexShrink: 0,
});

const legendLabelStyle: React.CSSProperties = {
  fontSize: "12px",
  color: "var(--text-secondary)",
  fontWeight: 700,
};

const legendValueStyle: React.CSSProperties = {
  fontSize: "22px",
  fontWeight: 900,
  color: "var(--text-primary)",
};

const buildSegmentPath = (
  cx: number,
  cy: number,
  r: number,
  startAngle: number,
  endAngle: number
) => {
  const start = polarToCartesian(cx, cy, r, endAngle);
  const end = polarToCartesian(cx, cy, r, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? 0 : 1;

  return [
    "M",
    start.x,
    start.y,
    "A",
    r,
    r,
    0,
    largeArcFlag,
    0,
    end.x,
    end.y,
  ].join(" ");
};

const polarToCartesian = (
  centerX: number,
  centerY: number,
  radius: number,
  angleInDegrees: number
) => {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;

  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  };
};

const DriverDeliveriesAnalytics = ({
  analytics,
}: DriverDeliveriesAnalyticsProps) => {
  const segments: Segment[] = [
    {
      label: "Entregados",
      value: analytics.status.delivered,
      color: "var(--success)",
    },
    {
      label: "Pendientes",
      value: analytics.status.pending,
      color: "var(--primary)",
    },
    {
      label: "Penalizados",
      value: analytics.status.penalized,
      color: "var(--danger)",
    },
  ].filter((segment) => segment.value > 0);

  const total = analytics.status.total || 1;
  const cx = 170;
  const cy = 170;
  const radius = 112;

  let currentAngle = 0;
  const gap = 7;

  return (
    <div style={cardStyle}>
      <div style={titleStyle}>Análisis semanal</div>
      <div style={subtitleStyle}>
        Distribución de envíos por estado
      </div>

      <div style={chartWrapStyle}>
        <div style={{ position: "relative", width: 340, height: 340 }}>
          <svg width="340" height="340" viewBox="0 0 340 340">
            {segments.map((segment, index) => {
              const rawAngle = (segment.value / total) * 360;
              const usableAngle = Math.max(rawAngle - gap, 8);
              const startAngle = currentAngle + gap / 2;
              const endAngle = startAngle + usableAngle;
              currentAngle += rawAngle;

              const path = buildSegmentPath(
                cx,
                cy,
                radius,
                startAngle,
                endAngle
              );

              return (
                <path
                  key={`${segment.label}-${index}`}
                  d={path}
                  fill="none"
                  stroke={segment.color}
                  strokeWidth="28"
                  strokeLinecap="round"
                />
              );
            })}
          </svg>

          <div style={centerTextWrapStyle}>
            <div style={centerLabelStyle}>Total</div>
            <div style={centerValueStyle}>{analytics.status.total}</div>
          </div>
        </div>
      </div>

      <div style={legendStyle}>
        <div style={legendCardStyle}>
          <div style={legendLabelRowStyle}>
            <span style={legendDotStyle("var(--success)")} />
            <span style={legendLabelStyle}>Entregados</span>
          </div>
          <div style={legendValueStyle}>{analytics.status.delivered}</div>
        </div>

        <div style={legendCardStyle}>
          <div style={legendLabelRowStyle}>
            <span style={legendDotStyle("var(--primary)")} />
            <span style={legendLabelStyle}>Pendientes</span>
          </div>
          <div style={legendValueStyle}>{analytics.status.pending}</div>
        </div>

        <div style={legendCardStyle}>
          <div style={legendLabelRowStyle}>
            <span style={legendDotStyle("var(--danger)")} />
            <span style={legendLabelStyle}>Penalizados</span>
          </div>
          <div style={legendValueStyle}>{analytics.status.penalized}</div>
        </div>
      </div>
    </div>
  );
};

export default DriverDeliveriesAnalytics;